import React, { useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { RxCross2 } from "react-icons/rx";
import CommonButton from "../../components/common/CommonButton";
import { fetchCcScreens } from "../../service/CallCenterRoleAdmin";
import ScreenPermissionsMatrix from "./ScreenPermissionsMatrix";
import {
  createEmptyScreenPermissions,
  screenPermissionsFromRole,
} from "../../utils/ccScreenPermissions";

const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

const emptyCreateForm = () => ({
  code: "",
  displayName: "",
  description: "",
  screenPermissions: {},
  active: true,
});

const emptyEditForm = () => ({
  displayName: "",
  description: "",
  screenPermissions: {},
  active: true,
});

const CcRoleFormModal = ({
  open,
  mode,
  role,
  onClose,
  onSave,
  loading,
  theme,
}) => {
  const isCreate = mode === "create";
  const isSystemRole = Boolean(role?.systemRole);
  const [screenDefs, setScreenDefs] = useState([]);
  const [form, setForm] = useState(isCreate ? emptyCreateForm() : emptyEditForm());

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchCcScreens()
      .then((list) => {
        if (!cancelled) setScreenDefs(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setScreenDefs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      setForm({
        ...emptyCreateForm(),
        screenPermissions: createEmptyScreenPermissions(screenDefs),
      });
      return;
    }
    if (role) {
      setForm({
        displayName: role.displayName || "",
        description: role.description || "",
        screenPermissions: screenPermissionsFromRole(screenDefs, role.screenPermissions),
        active: role.active !== false,
      });
    }
  }, [open, isCreate, role, screenDefs]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const inputClass = `w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    theme === "dark" ? "dark-mode border-gray-600" : theme === "high-contrast" ? "high-contrast" : "border-gray-300"
  }`;

  const permissionsDisabled = !isCreate && isSystemRole;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/55 backdrop-blur-[2px]" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`relative rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col ${
                  theme === "dark" ? "dark-mode bg-gray-800" : theme === "high-contrast" ? "high-contrast bg-white" : "bg-white"
                }`}
              >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {isCreate ? "Create role" : `Edit role: ${role?.code || ""}`}
                  </Dialog.Title>
                  <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">
                    <RxCross2 size={22} />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                  <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
                    {isCreate && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role code *</label>
                        <input
                          type="text"
                          className={inputClass}
                          placeholder="TEAM_LEAD"
                          value={form.code}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "") }))
                          }
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">Uppercase letters, numbers, underscores (e.g. TEAM_LEAD)</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Display name *</label>
                      <input
                        type="text"
                        className={inputClass}
                        value={form.displayName}
                        onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                        maxLength={255}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        className={`${inputClass} min-h-[80px]`}
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        maxLength={2000}
                        rows={3}
                      />
                    </div>
                    {!isCreate && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="role-active"
                          checked={form.active}
                          disabled={isSystemRole}
                          onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                          className="w-4 h-4"
                        />
                        <label htmlFor="role-active" className="text-sm text-gray-700">
                          Active
                          {isSystemRole && <span className="text-gray-500 ml-1">(built-in roles cannot be deactivated)</span>}
                        </label>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Screen permissions *
                        {isSystemRole && !isCreate && (
                          <span className="font-normal text-gray-500 ml-1">(read-only for built-in roles)</span>
                        )}
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Select screens and grant Read, Create, Update, or Delete access for each.
                      </p>
                      <ScreenPermissionsMatrix
                        screenDefs={screenDefs}
                        permissions={form.screenPermissions}
                        disabled={permissionsDisabled}
                        theme={theme}
                        onChange={(nextPermissions) =>
                          setForm((prev) => ({ ...prev, screenPermissions: nextPermissions }))
                        }
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200">
                    <CommonButton variant="danger" type="button" onClick={onClose}>
                      Cancel
                    </CommonButton>
                    <CommonButton type="submit" variant="primary" disabled={loading}>
                      {loading ? "Saving…" : isCreate ? "Create" : "Save"}
                    </CommonButton>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export { CODE_PATTERN };
export default CcRoleFormModal;
